import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import useSettingsStore from '../store/useSettingsStore';

const navLinks = [
  { path: '/', label: 'Главная' },
  { path: '/catalog', label: 'Каталог' },
  { path: '/configurator', label: 'Подбор' },
  { path: '/services', label: 'Услуги' },
  { path: '/reviews', label: 'Отзывы' },
  { path: '/blog', label: 'Блог' },
  { path: '/about', label: 'О нас' },
  { path: '/contacts', label: 'Контакты' },
];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { settings, siteContent, getImageUrl, setFeedbackOpen } = useSettingsStore();

  const siteName = siteContent?.header?.site_name || settings?.site_title || 'Euro Drive Import';
  const contactBtn = siteContent?.header?.contact_button || 'Связаться';
  const logoUrl = settings?.logo_url ? getImageUrl(settings.logo_url) : '/logo.png';

  return (
    <header className={`header-sticky fixed top-0 left-0 right-0 ${mobileOpen ? 'z-[9999]' : 'z-50'}`} data-testid="header">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="header-logo">
            {logoUrl && (
              <img src={logoUrl} alt={siteName} className="h-10 w-10 md:h-12 md:w-12 rounded-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
            )}
            <span className="text-base md:text-lg font-semibold" style={{ color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)' }}>
              {siteName}
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                className="px-3 py-2 rounded-md text-sm font-medium transition-colors duration-300"
                style={{
                  color: location.pathname === link.path ? 'var(--color-accent-gold)' : 'var(--color-text-secondary)',
                }}
                data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* CTA + Burger */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => { setFeedbackOpen(true); }}
              className="btn-gold text-sm py-2 px-4 !hidden md:!inline-flex"
              data-testid="header-contact-btn"
            >
              <Phone size={16} />
              {contactBtn}
            </button>
            <button
              className="lg:hidden burger-btn"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu - rendered via Portal to escape header stacking context */}
      {ReactDOM.createPortal(
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden fixed inset-0"
              style={{ background: '#070E1E', zIndex: 9998, paddingTop: '64px' }}
              data-testid="mobile-menu"
            >
              <nav className="flex flex-col p-6 gap-2">
                {navLinks.map((link, i) => (
                  <motion.div
                    key={link.path}
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.04, duration: 0.25 }}
                  >
                    <Link
                      to={link.path}
                      onClick={() => setMobileOpen(false)}
                      className="block py-3 px-4 rounded-lg text-lg font-medium transition-colors duration-300"
                      style={{
                        color: location.pathname === link.path ? 'var(--color-accent-gold)' : 'var(--color-text-primary)',
                        background: location.pathname === link.path ? 'rgba(255,255,255,0.05)' : 'transparent',
                      }}
                      data-testid={`mobile-nav-${link.path.replace('/', '') || 'home'}`}
                    >
                      {link.label}
                    </Link>
                  </motion.div>
                ))}
                <motion.button
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.35 }}
                  onClick={() => { setMobileOpen(false); setFeedbackOpen(true); }}
                  className="btn-gold mt-4 justify-center"
                  data-testid="mobile-contact-btn"
                >
                  <Phone size={18} />
                  {contactBtn}
                </motion.button>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </header>
  );
}
